import {
  DeesElement,
  property,
  html,
  customElement,
  type TemplateResult,
  css,
  cssManager,
  state,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { themeDefaultStyles } from '../../00theme.js';
import type { IExecutionEnvironment, IFileWatcher } from '../../00group-runtime/index.js';
import { workspaceStyles } from './styles.js';
import { demoFunc } from './dees-workspace.demo.js';
import type { IMenuItem } from '../../interfaces/tab.js';
import '../../00group-appui/dees-appui-tabs/dees-appui-tabs.js';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import '../../00group-button/dees-button/dees-button.js';
import '../dees-workspace-monaco/dees-workspace-monaco.js';
import '../dees-workspace-filetree/dees-workspace-filetree.js';
import { DeesWorkspaceFiletree } from '../dees-workspace-filetree/dees-workspace-filetree.js';
import '../dees-workspace-terminal/dees-workspace-terminal.js';
import { DeesWorkspaceTerminal } from '../dees-workspace-terminal/dees-workspace-terminal.js';
import type { IRunProcessEventDetail } from '../dees-workspace-terminal/interfaces.js';
import '../dees-workspace-terminal-preview/dees-workspace-terminal-preview.js';
import '../dees-workspace-bottombar/dees-workspace-bottombar.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import { DeesWorkspaceMonaco } from '../dees-workspace-monaco/dees-workspace-monaco.js';
import { TypeScriptIntelliSenseManager } from './typescript-intellisense.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import '@design.estate/dees-element/demotools';
import '../../00group-feedback/dees-actionbar/dees-actionbar.js';
import type { DeesActionbar } from '../../00group-feedback/dees-actionbar/dees-actionbar.js';
import '../dees-workspace-diff-editor/dees-workspace-diff-editor.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-workspace': DeesWorkspace;
  }
}

interface IOpenFile {
  path: string;
  name: string;
  content: string;
  modified: boolean;
}

@customElement('dees-workspace')
export class DeesWorkspace extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Workspace'];

  // INSTANCE
  @property({ type: Object })
  accessor executionEnvironment: IExecutionEnvironment | null = null;

  @property({ attribute: false })
  accessor initializationPromise: Promise<void> | null = null;

  @property({ type: String })
  accessor workspaceName = 'Workspace';

  /** A document to open once the supplied environment is ready. */
  @property({ type: String })
  accessor initialFilePath = '';

  @property({ type: Boolean })
  accessor showFileTree: boolean = true;

  @property({ type: Boolean })
  accessor showTerminal: boolean = true;

  @property({ type: Number })
  accessor fileTreeWidth: number = 250;

  @property({ type: Number })
  accessor terminalHeight: number = 200;

  @state()
  accessor openFiles: IOpenFile[] = [];

  @state()
  accessor activeFilePath: string = '';

  @state()
  accessor isTerminalCollapsed: boolean = false;

  @state()
  accessor isFileTreeCollapsed: boolean = false;

  @state()
  accessor isInitializing: boolean = false;

  @state() accessor initializationError = '';
  @state() accessor operationError = '';
  @state() private accessor narrow = false;
  private layoutObserver: ResizeObserver | null = null;
  private documentTabs: IMenuItem[] = [];

  @state()
  accessor activeBottomPanel: 'terminal' | 'problems' = 'terminal';

  @state()
  accessor diagnosticMarkers: Array<{
    message: string;
    severity: number;
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
    source?: string;
    resource: { path: string };
  }> = [];

  private editorElement: DeesWorkspaceMonaco | null = null;
  private initializationStarted: boolean = false;
  private intelliSenseManager: TypeScriptIntelliSenseManager | null = null;
  private intelliSenseInitialized: boolean = false;

  // node_modules watcher for auto-loading types
  private nodeModulesWatcher: IFileWatcher | null = null;
  private nodeModulesDebounceTimeout: ReturnType<typeof setTimeout> | null = null;
  private intelliSenseDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

  // Open file watchers for external change detection
  private openFileWatchers: Map<string, IFileWatcher> = new Map();
  private fileChangeDebounce: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private actionbarElement: DeesActionbar | null = null;

  // Auto-save functionality
  @state()
  accessor autoSave: boolean = false;
  private autoSaveInterval: ReturnType<typeof setInterval> | null = null;

  // Init terminal preview state
  @state()
  accessor initCommand: string = '';

  @state()
  accessor initOutput: string[] = [];

  // Resize state
  @state()
  accessor currentFileTreeWidth: number = 250;

  @state()
  accessor currentTerminalHeight: number = 200;

  @state()
  accessor isDraggingFileTree: boolean = false;

  @state()
  accessor isDraggingTerminal: boolean = false;

  // Diff view state
  @state()
  accessor showDiffView: boolean = false;

  @state()
  accessor diffViewConfig: {
    filePath: string;
    originalContent: string;
    modifiedContent: string;
    language: string;
  } | null = null;

  // Keyboard shortcut handler (bound for proper cleanup)
  private keydownHandler = (e: KeyboardEvent) => {
    if (!e.composedPath().includes(this)) return;
    // Cmd+S (Mac) or Ctrl+S (Windows/Linux) - Save
    if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.shiftKey) {
      e.preventDefault();
      this.saveActiveFile();
    }

    // Cmd+Shift+S - Save All
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 's') {
      e.preventDefault();
      this.saveAllFiles();
    }
  };

  // ========== Filetree Resize Handlers ==========
  private handleFileTreeMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    this.isDraggingFileTree = true;
    document.addEventListener('pointermove', this.handleFileTreeMouseMove);
    document.addEventListener('pointerup', this.handleFileTreeMouseUp);
    document.addEventListener('pointercancel', this.handleFileTreeMouseUp);
  };

  private handleFileTreeMouseMove = (e: MouseEvent) => {
    if (!this.isDraggingFileTree) return;

    const containerRect = this.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;

    // Clamp to min/max (150px min, 50% of container max)
    const minWidth = 150;
    const maxWidth = containerRect.width * 0.5;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, mouseX));

    this.currentFileTreeWidth = newWidth;
  };

  private handleFileTreeMouseUp = () => {
    this.isDraggingFileTree = false;
    document.removeEventListener('pointermove', this.handleFileTreeMouseMove);
    document.removeEventListener('pointerup', this.handleFileTreeMouseUp);
    document.removeEventListener('pointercancel', this.handleFileTreeMouseUp);

    // Notify Monaco editor of size change
    window.dispatchEvent(new Event('resize'));
  };

  // ========== Terminal Resize Handlers ==========
  private handleTerminalMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    this.isDraggingTerminal = true;
    document.addEventListener('pointermove', this.handleTerminalMouseMove);
    document.addEventListener('pointerup', this.handleTerminalMouseUp);
    document.addEventListener('pointercancel', this.handleTerminalMouseUp);
  };

  private handleTerminalMouseMove = (e: MouseEvent) => {
    if (!this.isDraggingTerminal) return;

    const containerRect = this.getBoundingClientRect();
    const mouseY = e.clientY - containerRect.top;

    // Calculate terminal height from bottom
    const terminalHeight = containerRect.height - mouseY;

    // Clamp to min/max (100px min, 70% of container max)
    const minHeight = 100;
    const maxHeight = containerRect.height * 0.7;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, terminalHeight));

    this.currentTerminalHeight = newHeight;
  };

  private handleTerminalMouseUp = () => {
    this.isDraggingTerminal = false;
    document.removeEventListener('pointermove', this.handleTerminalMouseMove);
    document.removeEventListener('pointerup', this.handleTerminalMouseUp);
    document.removeEventListener('pointercancel', this.handleTerminalMouseUp);

    // Notify Monaco editor of size change
    window.dispatchEvent(new Event('resize'));
  };

  public static styles = [themeDefaultStyles, cssManager.defaultStyles, workspaceStyles];

  private handleFileTreeResizeKey = (event: KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    this.setFileTreeWidth(event.key === 'Home' ? 150 : event.key === 'End' ? this.clientWidth / 2 : this.currentFileTreeWidth + (event.key === 'ArrowRight' ? 16 : -16));
  };

  private handleTerminalResizeKey = (event: KeyboardEvent) => {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    this.setTerminalHeight(event.key === 'Home' ? 100 : event.key === 'End' ? this.clientHeight * .7 : this.currentTerminalHeight + (event.key === 'ArrowUp' ? 16 : -16));
  };

  public render(): TemplateResult {
    const containerClasses = [
      'workspace-container',
      (this.isDraggingFileTree || this.isDraggingTerminal) ? 'dragging' : '',
    ].filter(Boolean).join(' ');

    const ready = !!this.executionEnvironment?.ready && !this.isInitializing && !this.initializationError;
    const modifiedCount = this.openFiles.filter(file => file.modified).length;

    return html`
      <div class="workspace-outer">
        <header class="workspace-toolbar">
          <div class="workspace-identity"><dees-icon icon="lucide:PanelsTopLeft" iconSize="18"></dees-icon><span>${this.workspaceName}</span></div>
          <span class="save-state" role="status">${this.isInitializing ? 'Starting…' : this.initializationError ? 'Connection failed' : !ready ? 'Not connected' : modifiedCount ? `${modifiedCount} unsaved` : 'All changes saved'}</span>
          <div class="toolbar-actions">
            <button class="panel-action" type="button" aria-label="Save all files" title="Save all files" ?disabled=${!ready || !modifiedCount} @click=${this.saveAllFiles}><dees-icon icon="lucide:Save" iconSize="16"></dees-icon></button>
            ${this.showFileTree ? html`<button class="panel-action panel-toggle" type="button" aria-label="Toggle files" aria-controls="workspace-files" aria-expanded=${!this.isFileTreeCollapsed} title="Toggle files" @click=${() => this.isFileTreeCollapsed = !this.isFileTreeCollapsed}><dees-icon icon="lucide:PanelLeft" iconSize="16"></dees-icon></button>` : ''}
            ${this.showTerminal ? html`<button class="panel-action panel-toggle" type="button" aria-label="Toggle terminal" aria-controls="workspace-terminal" aria-expanded=${!this.isTerminalCollapsed} title="Toggle terminal" @click=${this.toggleTerminal}><dees-icon icon="lucide:PanelBottom" iconSize="16"></dees-icon></button>` : ''}
            <button class="panel-action" type="button" aria-label="Editor options" aria-haspopup="menu" title="Editor options" @click=${this.showEditorMenu}><dees-icon icon="lucide:Ellipsis" iconSize="16"></dees-icon></button>
          </div>
        </header>
        ${this.operationError ? html`<div class="status-caption" role="alert">${this.operationError}</div>` : ''}
        <div class="${containerClasses}">
          <!-- Filetree panel (full height) -->
          ${this.showFileTree ? html`
            <div
              id="workspace-files"
              class="filetree-panel ${this.isFileTreeCollapsed ? 'collapsed' : ''}"
              style="width: ${this.isFileTreeCollapsed ? 0 : this.currentFileTreeWidth}px"
            >
              ${ready ? html`<dees-workspace-filetree
                .executionEnvironment=${this.executionEnvironment}
                .selectedPath=${this.activeFilePath}
                @file-select=${this.handleFileSelect}
                @file-created=${(event: CustomEvent<{ path: string }>) => this.openFile(event.detail.path)}
              ></dees-workspace-filetree>` : html`<div class="status-caption">${this.isInitializing ? 'Loading project files…' : 'Project files appear when connected.'}</div>`}
            </div>
            ${!this.isFileTreeCollapsed ? html`
              <div
                class="resize-handle-vertical ${this.isDraggingFileTree ? 'dragging' : ''}"
                role="separator" aria-label="Resize files panel" aria-orientation="vertical" tabindex="0"
                aria-valuemin="150" aria-valuemax=${Math.max(150, Math.floor(this.clientWidth / 2))} aria-valuenow=${Math.round(this.currentFileTreeWidth)}
                @keydown=${this.handleFileTreeResizeKey}
                @pointerdown=${this.handleFileTreeMouseDown}
              ></div>
            ` : ''}
            ${this.narrow && !this.isFileTreeCollapsed ? html`<button class="files-dismiss" type="button" aria-label="Close files" @click=${() => this.isFileTreeCollapsed = true}></button>` : ''}
          ` : ''}

          <!-- Editor + Terminal area -->
          <div class="editor-area">
            <div class="editor-panel">
              <dees-appui-tabs class="document-tabs" .tabs=${this.documentTabs} .selectedTab=${this.documentTabs.find(tab => tab.key === this.activeFilePath) ?? null}
                .actionsRight=${this.showFileTree && this.isFileTreeCollapsed ? [{
                  id: 'new-file', iconName: 'lucide:Plus', tooltip: 'New file', disabled: !ready,
                  action: async () => { await this.shadowRoot?.querySelector<DeesWorkspaceFiletree>('dees-workspace-filetree')?.createNewFile(); },
                }] : []}></dees-appui-tabs>
              ${this.activeFilePath ? html`<div class="document-path"><dees-icon icon="lucide:FileCode" iconSize="12"></dees-icon><span>${this.activeFilePath}</span></div>` : ''}
              <div class="editor-content">
                ${!ready ? html`<div class="empty-state ${this.isInitializing ? 'initializing' : ''}" role=${this.initializationError ? 'alert' : 'status'}>
                  <dees-icon .icon=${this.isInitializing ? 'lucide:LoaderCircle' : this.initializationError ? 'lucide:CircleAlert' : 'lucide:PanelsTopLeft'} iconSize="32"></dees-icon>
                  <strong>${this.isInitializing ? 'Opening your workspace' : this.initializationError ? 'Unable to open workspace' : 'Your workspace, ready to connect'}</strong>
                  <p>${this.initializationError || (this.isInitializing ? 'Preparing the project and its runtime.' : 'Connect an execution environment to browse files and run commands.')}</p>
                  ${this.initializationError ? html`<dees-button size="small" @clicked=${() => this.initializeWorkspace()}>Try again</dees-button>` : ''}
                </div>` : this.showDiffView && this.diffViewConfig ? html`
                  <dees-workspace-diff-editor
                    .filePath=${this.diffViewConfig.filePath}
                    .originalContent=${this.diffViewConfig.originalContent}
                    .modifiedContent=${this.diffViewConfig.modifiedContent}
                    .language=${this.diffViewConfig.language}
                    @diff-resolved=${this.handleDiffResolved}
                    @diff-closed=${() => { this.showDiffView = false; this.diffViewConfig = null; }}
                  ></dees-workspace-diff-editor>
                ` : this.openFiles.length === 0 ? html`
                  <div class="empty-state">
                    <dees-icon .icon=${'lucide:fileCode'} iconSize="48"></dees-icon>
                    <strong>Make something good.</strong>
                    <p>Open a file from the explorer to start editing.</p>
                    ${this.showFileTree ? html`<dees-button size="small" buttonType="discreet" @clicked=${() => this.isFileTreeCollapsed = false}>Browse files</dees-button>` : ''}
                  </div>
                ` : html`
                  <dees-workspace-monaco
                    .filePath=${this.activeFilePath}
                    .content=${this.getActiveFileContent()}
                    .language=${this.getLanguageFromPath(this.activeFilePath)}
                    @content-change=${this.handleContentChange}
                  ></dees-workspace-monaco>
                `}
              </div>
              <dees-actionbar></dees-actionbar>
            </div>

            <!-- Horizontal resize handle for terminal -->
            ${this.showTerminal && !this.isTerminalCollapsed ? html`
              <div
                class="resize-handle-horizontal ${this.isDraggingTerminal ? 'dragging' : ''}"
                role="separator" aria-label="Resize terminal panel" aria-orientation="horizontal" tabindex="0"
                aria-valuemin="100" aria-valuemax=${Math.max(100, Math.floor(this.clientHeight * .7))} aria-valuenow=${Math.round(this.currentTerminalHeight)}
                @keydown=${this.handleTerminalResizeKey}
                @pointerdown=${this.handleTerminalMouseDown}
              ></div>
            ` : ''}

            <!-- Terminal panel -->
            ${this.showTerminal ? html`
              <div
                id="workspace-terminal"
                class="terminal-panel ${this.isTerminalCollapsed ? 'collapsed' : ''}"
                style="height: ${this.currentTerminalHeight}px"
              >
                <div class="panel-header">
                  <dees-input-multitoggle size="sm" label="Output panel" labelPosition="none"
                    .options=${[{ key: 'terminal', label: 'Terminal' }, { key: 'problems', label: `Problems${this.diagnosticMarkers.length ? ` · ${this.diagnosticMarkers.length}` : ''}` }]}
                    .selectedOption=${this.activeBottomPanel}
                    @option-activate=${(e: CustomEvent<{ value: string }>) => { this.activeBottomPanel = e.detail.value as 'terminal' | 'problems'; this.isTerminalCollapsed = false; }}
                  ></dees-input-multitoggle>
                  <div class="panel-header-actions">
                    <button type="button" class="panel-action" aria-label=${this.isTerminalCollapsed ? 'Expand output panel' : 'Collapse output panel'} aria-expanded=${!this.isTerminalCollapsed} @click=${this.toggleTerminal}>
                      <dees-icon
                        .icon=${this.isTerminalCollapsed ? 'lucide:chevronUp' : 'lucide:chevronDown'}
                        iconSize="14"
                      ></dees-icon>
                    </button>
                  </div>
                </div>
                <div class="terminal-content" ?hidden=${this.activeBottomPanel !== 'terminal' || this.isTerminalCollapsed}>
                  ${ready ? html`<dees-workspace-terminal
                    .executionEnvironment=${this.executionEnvironment}
                    .setupCommand=${''}
                  ></dees-workspace-terminal>` : this.initCommand ? html`<dees-workspace-terminal-preview .command=${this.initCommand} .lines=${this.initOutput}></dees-workspace-terminal-preview>` : html`<div class="status-caption">${this.isInitializing ? 'Starting runtime…' : 'Terminal is not connected.'}</div>`}
                </div>
                <div class="problems-content" ?hidden=${this.activeBottomPanel !== 'problems' || this.isTerminalCollapsed}>
                  ${this.renderProblemsPanel()}
                </div>
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Bottom Bar (full width) -->
        <dees-workspace-bottombar
          .executionEnvironment=${ready ? this.executionEnvironment : null}
          @run-process=${this.handleRunProcess}
        ></dees-workspace-bottombar>
      </div>
    `;
  }

  async connectedCallback() {
    await super.connectedCallback();
    if (!this.isConnected) return;
    document.addEventListener('keydown', this.keydownHandler);
    this.layoutObserver = new ResizeObserver(([entry]) => {
      const next = entry.contentRect.width <= 680;
      if (next !== this.narrow) { this.narrow = next; this.isFileTreeCollapsed = next; }
    });
    this.layoutObserver.observe(this);
  }

  async disconnectedCallback() {
    await super.disconnectedCallback();
    document.removeEventListener('keydown', this.keydownHandler);
    this.layoutObserver?.disconnect();
    this.layoutObserver = null;

    // Clean up resize event listeners
    document.removeEventListener('pointermove', this.handleFileTreeMouseMove);
    document.removeEventListener('pointerup', this.handleFileTreeMouseUp);
    document.removeEventListener('pointercancel', this.handleFileTreeMouseUp);
    document.removeEventListener('pointermove', this.handleTerminalMouseMove);
    document.removeEventListener('pointerup', this.handleTerminalMouseUp);
    document.removeEventListener('pointercancel', this.handleTerminalMouseUp);

    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
    this.stopNodeModulesWatcher();
    this.stopAllFileWatchers();
  }

  public async firstUpdated() {
    // Initialize current sizes from properties
    this.currentFileTreeWidth = this.fileTreeWidth;
    this.currentTerminalHeight = this.terminalHeight;

    if (this.executionEnvironment) {
      await this.initializeWorkspace();
    }
  }

  protected willUpdate(changed: Map<PropertyKey, unknown>) {
    if (changed.has('openFiles')) {
      this.documentTabs = this.openFiles.map(file => ({
        key: file.path, label: file.name, badge: file.modified ? 'Edited' : undefined,
        action: () => this.activateFile(file.path), closeable: true,
        onClose: () => this.closeFile(new Event('close'), file.path),
      }));
    }
  }

  public async updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('executionEnvironment') && this.executionEnvironment) {
      await this.initializeWorkspace();
    }

    // Capture actionbar reference when it becomes available (after initialization completes)
    if (!this.actionbarElement) {
      this.actionbarElement = this.shadowRoot?.querySelector('.editor-panel dees-actionbar') as DeesActionbar;
    }
  }

  private async initializeWorkspace() {
    if (!this.executionEnvironment) return;

    // Prevent double initialization
    if (this.initializationStarted) return;
    this.initializationStarted = true;

    this.isInitializing = true;
    this.initializationError = '';

    try {
      // Wait for any external initialization (e.g., file mounting)
      if (this.initializationPromise) {
        await this.initializationPromise;
      } else if (!this.executionEnvironment.ready) {
        await this.executionEnvironment.init();
      }

      // Execute onInit command from npmextra.json if present
      await this.executeOnInitCommand();
      this.isInitializing = false;
      if (this.isConnected && this.initialFilePath) await this.openFile(this.initialFilePath);

      // IntelliSense is initialized lazily when first file is opened (Monaco loads on demand)
    } catch (error) {
      console.error('Failed to initialize workspace:', error);
      this.initializationError = error instanceof Error ? error.message : String(error);
      // Reset flag to allow retry
      this.initializationStarted = false;
    } finally {
      this.isInitializing = false;
    }
  }

  /**
   * Execute onInit command from npmextra.json if present
   * This allows automatic setup like `pnpm install` on workspace initialization
   */
  private async executeOnInitCommand(): Promise<void> {
    if (!this.executionEnvironment) return;

    try {
      if (await this.executionEnvironment.exists('/npmextra.json')) {
        const content = await this.executionEnvironment.readFile('/npmextra.json');
        const config = JSON.parse(content);
        const onInit = config?.deesEditorWorkspace?.onInit;

        if (onInit && typeof onInit === 'string') {
          console.log('Executing onInit command:', onInit);

          // Set command for terminal preview display
          this.initCommand = onInit;
          this.initOutput = [];

          // Parse command and args
          const [cmd, ...args] = onInit.split(' ');
          const process = await this.executionEnvironment.spawn(cmd, args);

          // Capture stdout/stderr for terminal preview
          const outputDone = process.output.pipeTo(
            new WritableStream({
              write: (data) => {
                // Split data into lines and append
                const lines = data.split('\n');
                for (const line of lines) {
                  if (line) {
                    this.initOutput = [...this.initOutput, line];
                  }
                }
              },
            })
          );

          // Wait for completion
          const exitCode = await process.exit;
          await outputDone;
          if (exitCode !== 0) throw new Error(`Workspace setup failed with exit code ${exitCode}.`);
          console.log('onInit command completed with exit code:', exitCode);

          // Add completion message
          this.initOutput = [...this.initOutput, '', `Command completed with exit code: ${exitCode}`];
        }
      }
    } catch (error) {
      throw error;
    }
  }

  private async initializeIntelliSense(): Promise<void> {
    if (!this.executionEnvironment) return;
    if (this.intelliSenseInitialized) return;

    // Wait for Monaco to be available globally (with retry for timing)
    let monacoInstance = (window as any).monaco;
    if (!monacoInstance) {
      // Monaco loads asynchronously when the editor mounts, wait a bit
      await new Promise(resolve => setTimeout(resolve, 100));
      monacoInstance = (window as any).monaco;
    }

    if (!monacoInstance) {
      console.warn('Monaco not yet loaded, IntelliSense will be initialized later');
      return;
    }

    this.intelliSenseInitialized = true;
    this.intelliSenseManager = new TypeScriptIntelliSenseManager();
    await this.intelliSenseManager.init(monacoInstance, this.executionEnvironment);

    // Set up marker listener for Problems panel
    this.setupMarkerListener();

    // Start watching node_modules for package installations
    this.startNodeModulesWatcher();

    // Initial scan for any existing packages
    await this.intelliSenseManager.scanAndLoadNewPackageTypes();
  }

  /**
   * Watch node_modules for changes (e.g., after pnpm install)
   * and automatically load types for new packages
   */
  private startNodeModulesWatcher(): void {
    if (!this.executionEnvironment || this.nodeModulesWatcher) return;

    try {
      this.nodeModulesWatcher = this.executionEnvironment.watch(
        '/node_modules',
        (_event, _filename) => {
          // Debounce - pnpm install creates many file changes
          if (this.nodeModulesDebounceTimeout) {
            clearTimeout(this.nodeModulesDebounceTimeout);
          }
          this.nodeModulesDebounceTimeout = setTimeout(async () => {
            if (this.intelliSenseManager) {
              await this.intelliSenseManager.scanAndLoadNewPackageTypes();
            }
          }, 2000); // 2 second debounce for package installation
        },
        { recursive: true }
      );
    } catch (error) {
      console.warn('Could not watch node_modules:', error);
    }
  }

  private stopNodeModulesWatcher(): void {
    if (this.nodeModulesWatcher) {
      this.nodeModulesWatcher.stop();
      this.nodeModulesWatcher = null;
    }
    if (this.nodeModulesDebounceTimeout) {
      clearTimeout(this.nodeModulesDebounceTimeout);
      this.nodeModulesDebounceTimeout = null;
    }
  }

  // ========== Open File Watching for External Changes ==========

  /**
   * Start watching an open file for external changes
   */
  private startWatchingFile(path: string): void {
    if (!this.executionEnvironment || this.openFileWatchers.has(path)) return;

    try {
      const watcher = this.executionEnvironment.watch(
        path,
        (_event, _filename) => {
          // Debounce to avoid multiple rapid triggers
          const existingTimeout = this.fileChangeDebounce.get(path);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }
          const timeout = setTimeout(() => {
            this.handleExternalFileChange(path);
            this.fileChangeDebounce.delete(path);
          }, 300);
          this.fileChangeDebounce.set(path, timeout);
        }
      );
      this.openFileWatchers.set(path, watcher);
    } catch (error) {
      console.warn(`Could not watch file ${path}:`, error);
    }
  }

  /**
   * Stop watching a file when it's closed
   */
  private stopWatchingFile(path: string): void {
    const watcher = this.openFileWatchers.get(path);
    if (watcher) {
      watcher.stop();
      this.openFileWatchers.delete(path);
    }
    const timeout = this.fileChangeDebounce.get(path);
    if (timeout) {
      clearTimeout(timeout);
      this.fileChangeDebounce.delete(path);
    }
  }

  /**
   * Stop all file watchers
   */
  private stopAllFileWatchers(): void {
    for (const watcher of this.openFileWatchers.values()) {
      watcher.stop();
    }
    this.openFileWatchers.clear();

    for (const timeout of this.fileChangeDebounce.values()) {
      clearTimeout(timeout);
    }
    this.fileChangeDebounce.clear();
  }

  /**
   * Handle external file change - show actionbar if file has local changes,
   * otherwise silently update with cursor preservation
   */
  private async handleExternalFileChange(path: string): Promise<void> {
    const file = this.openFiles.find(f => f.path === path);
    if (!file || !this.executionEnvironment) return;

    try {
      // Read the new content from disk
      const newContent = await this.executionEnvironment.readFile(path);

      // If content is same as what we have, no action needed
      if (newContent === file.content) return;

      if (file.modified) {
        // File has unsaved local changes AND disk changed - conflict!
        const result = await this.actionbarElement?.show({
          message: `"${file.name}" changed on disk. What do you want to do?`,
          type: 'question',
          icon: 'lucide:gitMerge',
          actions: [
            { id: 'load-disk', label: 'Load from Disk', primary: true },
            { id: 'save-local', label: 'Save Local to Disk' },
            { id: 'compare', label: 'Compare' },
          ],
          dismissible: true,
        });

        if (result?.actionId === 'load-disk') {
          // Discard local changes, load disk version
          await this.updateFileContent(path, newContent, false);
        } else if (result?.actionId === 'save-local') {
          // Keep local changes and save to disk (overwrite external)
          await this.executionEnvironment.writeFile(path, file.content);
          // Mark as saved
          this.openFiles = this.openFiles.map(f =>
            f.path === path ? { ...f, modified: false } : f
          );
        } else if (result?.actionId === 'compare') {
          // Open diff view
          this.openDiffView(path, file.content, newContent);
        }
        // If dismissed, do nothing - user can manually resolve later
      } else {
        // No local changes - silently update with cursor preservation
        await this.updateFileContent(path, newContent, true);
      }
    } catch (error) {
      console.warn(`Failed to handle external change for ${path}:`, error);
    }
  }

  /**
   * Update file content in state and optionally in the editor
   */
  private async updateFileContent(
    path: string,
    newContent: string,
    preserveCursor: boolean
  ): Promise<void> {
    // Update internal state
    this.openFiles = this.openFiles.map(f =>
      f.path === path ? { ...f, content: newContent, modified: false } : f
    );

    // If this is the active file, update Monaco editor
    if (path === this.activeFilePath) {
      const editor = this.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;
      if (editor) {
        await editor.setContentExternal(newContent, preserveCursor);
      }
    }
  }

  /**
   * Open the diff view to compare local and disk versions
   */
  private openDiffView(path: string, localContent: string, diskContent: string): void {
    this.diffViewConfig = {
      filePath: path,
      originalContent: diskContent,
      modifiedContent: localContent,
      language: this.getLanguageFromPath(path),
    };
    this.showDiffView = true;
  }

  /**
   * Handle diff view resolution
   */
  private async handleDiffResolved(e: CustomEvent): Promise<void> {
    const { action, content } = e.detail;
    const path = this.diffViewConfig?.filePath;

    if (!path || !this.executionEnvironment) {
      this.showDiffView = false;
      this.diffViewConfig = null;
      return;
    }

    if (action === 'use-local') {
      // Save local content to disk
      await this.executionEnvironment.writeFile(path, content);
      this.openFiles = this.openFiles.map(f =>
        f.path === path ? { ...f, content, modified: false } : f
      );
      // Update editor if active
      if (path === this.activeFilePath) {
        const editor = this.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;
        if (editor) {
          await editor.setContentExternal(content, false);
        }
      }
    } else if (action === 'use-disk') {
      // Update editor with disk content
      await this.updateFileContent(path, content, false);
    }

    this.showDiffView = false;
    this.diffViewConfig = null;
  }

  private async handleFileSelect(e: CustomEvent<{ path: string; name: string }>) {
    const { path, name } = e.detail;
    await this.openFile(path, name);
    if (this.narrow) this.isFileTreeCollapsed = true;
  }

  public async openFile(path: string, name = path.split('/').pop() || path) {
    // Check if already open
    const existingFile = this.openFiles.find(f => f.path === path);
    if (existingFile) {
      this.activeFilePath = path;
      return;
    }

    // Load file content
    if (!this.executionEnvironment) return;

    try {
      const content = await this.executionEnvironment.readFile(path);
      this.openFiles = [
        ...this.openFiles,
        { path, name, content, modified: false },
      ];
      this.activeFilePath = path;

      // Start watching for external changes
      this.startWatchingFile(path);

      // Initialize IntelliSense lazily after first file opens (Monaco loads on demand)
      if (!this.intelliSenseInitialized) {
        // Wait for Monaco editor to mount and load Monaco from CDN
        await this.updateComplete;
        // Give Monaco time to load via require.js
        await new Promise(resolve => setTimeout(resolve, 500));
        await this.initializeIntelliSense();

        // Process the initial file content for IntelliSense
        const language = this.getLanguageFromPath(path);
        if (this.intelliSenseManager && (language === 'typescript' || language === 'javascript')) {
          await this.intelliSenseManager.processContentChange(content);
        }
      }
    } catch (error) {
      console.error(`Failed to open file ${path}:`, error);
    }
  }

  private activateFile(path: string) {
    this.activeFilePath = path;
  }

  private closeFile(e: Event, path: string) {
    e.stopPropagation();

    const fileIndex = this.openFiles.findIndex(f => f.path === path);
    if (fileIndex === -1) return;

    // Check for unsaved changes
    const file = this.openFiles[fileIndex];
    if (file.modified) {
      const confirmed = confirm(`${file.name} has unsaved changes. Close anyway?`);
      if (!confirmed) return;
    }

    // Stop watching this file
    this.stopWatchingFile(path);

    this.openFiles = this.openFiles.filter(f => f.path !== path);

    // If closing the active file, activate another one
    if (this.activeFilePath === path) {
      if (this.openFiles.length > 0) {
        const newIndex = Math.min(fileIndex, this.openFiles.length - 1);
        this.activeFilePath = this.openFiles[newIndex].path;
      } else {
        this.activeFilePath = '';
      }
    }
  }

  private getActiveFileContent(): string {
    const file = this.openFiles.find(f => f.path === this.activeFilePath);
    return file?.content || '';
  }

  private handleContentChange(e: CustomEvent) {
    const newContent = e.detail;
    const fileIndex = this.openFiles.findIndex(f => f.path === this.activeFilePath);
    if (fileIndex === -1) return;

    const file = this.openFiles[fileIndex];
    if (file.content !== newContent) {
      this.openFiles = [
        ...this.openFiles.slice(0, fileIndex),
        { ...file, content: newContent, modified: true },
        ...this.openFiles.slice(fileIndex + 1),
      ];

      // Process content for IntelliSense (debounced to avoid parsing on every keystroke)
      const language = this.getLanguageFromPath(this.activeFilePath);
      if (this.intelliSenseManager && (language === 'typescript' || language === 'javascript')) {
        if (this.intelliSenseDebounceTimeout) {
          clearTimeout(this.intelliSenseDebounceTimeout);
        }
        this.intelliSenseDebounceTimeout = setTimeout(() => {
          this.intelliSenseManager?.processContentChange(newContent);
        }, 500); // 500ms debounce
      }
    }
  }

  private getLanguageFromPath(path: string): string {
    const ext = path.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      ts: 'typescript',
      tsx: 'typescript',
      js: 'javascript',
      jsx: 'javascript',
      json: 'json',
      html: 'html',
      css: 'css',
      scss: 'scss',
      less: 'less',
      md: 'markdown',
      yaml: 'yaml',
      yml: 'yaml',
      xml: 'xml',
      sql: 'sql',
      py: 'python',
      sh: 'shell',
      bash: 'shell',
    };
    return languageMap[ext || ''] || 'plaintext';
  }

  private toggleTerminal() {
    this.isTerminalCollapsed = !this.isTerminalCollapsed;
  }

  // ========== Save Operations ==========

  public async saveActiveFile(): Promise<void> {
    const file = this.openFiles.find(f => f.path === this.activeFilePath);
    if (!file || !this.executionEnvironment) return;
    this.operationError = '';

    try {
      await this.executionEnvironment.writeFile(file.path, file.content);

      // Update file state to mark as saved
      this.openFiles = this.openFiles.map(f =>
        f.path === file.path && f.content === file.content ? { ...f, modified: false } : f
      );

      // Update IntelliSense manager with latest content
      if (this.intelliSenseManager) {
        this.intelliSenseManager.addFileModel(file.path, file.content);
      }
    } catch (error) {
      this.operationError = `Could not save ${file.name}. Your edits are still open.`;
      console.error('Failed to save file:', error);
    }
  }

  public async saveAllFiles(): Promise<void> {
    if (!this.executionEnvironment) return;
    this.operationError = '';
    const failed: string[] = [];

    for (const file of this.openFiles.filter(f => f.modified)) {
      try {
        await this.executionEnvironment.writeFile(file.path, file.content);
        this.openFiles = this.openFiles.map(current => current.path === file.path && current.content === file.content ? { ...current, modified: false } : current);

        // Update IntelliSense manager
        if (this.intelliSenseManager) {
          this.intelliSenseManager.addFileModel(file.path, file.content);
        }
      } catch (error) {
        failed.push(file.name);
        console.error(`Failed to save ${file.path}:`, error);
      }
    }

    if (failed.length) this.operationError = `Could not save ${failed.join(', ')}. Your edits are still open.`;
  }

  // ========== Editor Menu ==========

  private async showEditorMenu(e: MouseEvent) {
    e.stopPropagation();

    const menuItems: Parameters<typeof DeesContextmenu.openContextMenuWithOptions>[1] = [
      {
        name: this.autoSave ? '✓ Auto Save' : 'Auto Save',
        iconName: 'lucide:save',
        action: async () => this.toggleAutoSave(),
      },
      { divider: true },
      {
        name: 'Save',
        iconName: 'lucide:save',
        action: async () => this.saveActiveFile(),
      },
      {
        name: 'Save All',
        iconName: 'lucide:save',
        action: async () => this.saveAllFiles(),
      },
    ];

    await DeesContextmenu.openContextMenuWithOptions(e, menuItems);
  }

  private toggleAutoSave() {
    this.autoSave = !this.autoSave;

    if (this.autoSave) {
      // Save every 2 seconds if there are changes
      this.autoSaveInterval = setInterval(() => {
        const hasUnsaved = this.openFiles.some(f => f.modified);
        if (hasUnsaved) {
          this.saveAllFiles();
        }
      }, 2000);
    } else {
      if (this.autoSaveInterval) {
        clearInterval(this.autoSaveInterval);
        this.autoSaveInterval = null;
      }
    }
  }

  private getErrorCount(): number {
    // Monaco MarkerSeverity: Error = 8, Warning = 4, Info = 2, Hint = 1
    return this.diagnosticMarkers.filter(m => m.severity === 8).length;
  }

  private renderProblemsPanel(): TemplateResult {
    if (this.diagnosticMarkers.length === 0) {
      return html`
        <div class="problems-empty">
          <dees-icon .icon=${'lucide:checkCircle'} iconSize="24"></dees-icon>
          <span>No problems detected</span>
        </div>
      `;
    }

    return html`
      <div class="problems-list">
        ${this.diagnosticMarkers.map(marker => html`
          <div class="problem-item" @click=${() => this.navigateToProblem(marker)}>
            <dees-icon
              class="problem-icon ${marker.severity === 8 ? 'error' : 'warning'}"
              .icon=${marker.severity === 8 ? 'lucide:circleX' : 'lucide:triangleAlert'}
              iconSize="14"
            ></dees-icon>
            <div class="problem-details">
              <div class="problem-message">${marker.message}</div>
              <div class="problem-location">
                ${marker.resource.path.split('/').pop()} (${marker.startLineNumber}, ${marker.startColumn})
                ${marker.source ? `[${marker.source}]` : ''}
              </div>
            </div>
          </div>
        `)}
      </div>
    `;
  }

  private async navigateToProblem(marker: typeof this.diagnosticMarkers[0]) {
    // Extract file path from resource
    const filePath = marker.resource.path;
    const fileName = filePath.split('/').pop() || '';

    // Open the file if not already open
    const existingFile = this.openFiles.find(f => f.path === filePath);
    if (!existingFile) {
      await this.openFile(filePath, fileName);
    } else {
      this.activeFilePath = filePath;
    }

    // Wait for editor to be ready, then navigate to the line
    await this.updateComplete;
    const editorElement = this.shadowRoot?.querySelector('dees-workspace-monaco') as DeesWorkspaceMonaco;
    if (editorElement) {
      const editor = await editorElement.editorDeferred.promise;
      editor.revealLineInCenter(marker.startLineNumber);
      editor.setPosition({
        lineNumber: marker.startLineNumber,
        column: marker.startColumn,
      });
      editor.focus();
    }
  }

  private setupMarkerListener() {
    const monacoInstance = (window as any).monaco;
    if (!monacoInstance) return;

    // Listen for marker changes
    monacoInstance.editor.onDidChangeMarkers((uris: any[]) => {
      this.updateDiagnosticMarkers();
    });

    // Initial load
    this.updateDiagnosticMarkers();
  }

  private updateDiagnosticMarkers() {
    const monacoInstance = (window as any).monaco;
    if (!monacoInstance) return;

    // Get all markers from Monaco
    const allMarkers = monacoInstance.editor.getModelMarkers({});

    // Transform to our format
    this.diagnosticMarkers = allMarkers.map((m: any) => ({
      message: m.message,
      severity: m.severity,
      startLineNumber: m.startLineNumber,
      startColumn: m.startColumn,
      endLineNumber: m.endLineNumber,
      endColumn: m.endColumn,
      source: m.source,
      resource: { path: m.resource.path },
    }));
  }

  // ========== Bottom Bar Event Handlers ==========

  /**
   * Handle run-process events from bottom bar widgets
   * Creates a new terminal tab for the process
   */
  private async handleRunProcess(e: CustomEvent<IRunProcessEventDetail>): Promise<void> {
    const detail = e.detail;

    // Find the terminal component
    const terminal = this.shadowRoot?.querySelector('dees-workspace-terminal') as DeesWorkspaceTerminal;
    if (!terminal) {
      console.warn('Terminal component not found');
      return;
    }

    // Expand terminal if collapsed
    if (this.isTerminalCollapsed) {
      this.isTerminalCollapsed = false;
    }

    // Switch to terminal panel
    this.activeBottomPanel = 'terminal';

    // Wait for UI update
    await this.updateComplete;

    // Create a new terminal tab for the process
    await terminal.createProcessTab({
      type: detail.type,
      label: detail.label,
      command: detail.command,
      args: detail.args,
      metadata: detail.metadata,
      switchToTab: true,
    });
  }

  // ========== Public Layout Methods ==========

  /**
   * Programmatically set the file tree width
   */
  public setFileTreeWidth(width: number): void {
    const minWidth = 150;
    const maxWidth = this.getBoundingClientRect().width * 0.5;
    this.currentFileTreeWidth = Math.max(minWidth, Math.min(maxWidth, width));
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Programmatically set the terminal height
   */
  public setTerminalHeight(height: number): void {
    const minHeight = 100;
    const maxHeight = this.getBoundingClientRect().height * 0.7;
    this.currentTerminalHeight = Math.max(minHeight, Math.min(maxHeight, height));
    window.dispatchEvent(new Event('resize'));
  }

  /**
   * Reset layout to initial property values
   */
  public resetLayout(): void {
    this.currentFileTreeWidth = this.fileTreeWidth;
    this.currentTerminalHeight = this.terminalHeight;
    window.dispatchEvent(new Event('resize'));
  }
}
